import { ClipSource, SpeechRequest } from './clipSource'
import { digestSpeechText } from './digest'
import { CLIP_STATE, ClipPayload, ClipState, FetchWatcher, plainFetch, WordSpan } from './speechEngine'
import { smoothWordSpans } from './wordSpans'
import { buildWordTimeline } from './speechText'

export type ClipEntry = ClipPayload | ClipState

export type MissListener = (source: ClipSource, textHash: string) => void

interface Lookup {
  entry: ClipEntry;
  unrecorded: boolean;
}

const ALIGNMENT_VERSION = 1
const AUDIO_EXTENSION = 'mp3'
const ALIGNMENT_EXTENSION = 'json'
const NOT_FOUND = 404

const entries = new Map<string, ClipEntry>()
let baseUrl: string | null = null
let tooSlow = false

const clipBaseUrl = () => {
  if (baseUrl === null) { baseUrl = String(window.postbuildEnv.READ_ALOUD_CLIP_BASE_URL || '').replace(/\/+$/, '') }
  return baseUrl
}

export const clipsConfigured = () => clipBaseUrl() !== ''

export const clipsTooSlow = () => tooSlow

export const markClipsTooSlow = () => { tooSlow = true }

export const clipObjectUrl = (hash: string, extension: string) => `${clipBaseUrl()}/${hash.slice(0, 2)}/${hash}.${extension}`

export const isClipPayload = (entry: ClipEntry | undefined): entry is ClipPayload => typeof entry === 'object' && entry !== null

const isWordSpan = (span: unknown): span is WordSpan => Array.isArray(span) && span.length === 2 && span.every(value => Number.isFinite(value))

const isAscendingWordIndexList = (list: unknown, wordCount: number) => Array.isArray(list)
  && list.every((index, position) => Number.isInteger(index) && index >= 0 && index < wordCount && (position === 0 || index > list[position - 1]))

const payloadFromDocument = (text: string, document: any, audioUrl: string): ClipPayload | null => {
  if (!document || document.v !== ALIGNMENT_VERSION || document.text !== text) { return null }

  const timeline = buildWordTimeline(text)
  const { sentenceEnds = [], words, } = document
  if (!Array.isArray(words) || words.length !== timeline.length || !words.every(isWordSpan)) { return null }
  if (!isAscendingWordIndexList(sentenceEnds, timeline.length)) { return null }

  return { sentenceEnds, timeline, url: audioUrl, words: smoothWordSpans(words), }
}

const missing = (unrecorded: boolean): Lookup => ({ entry: CLIP_STATE.MISSING, unrecorded, })

const fetchClip = async (text: string, hash: string, watch: FetchWatcher): Promise<Lookup> => {
  try {
    const response = await watch(clipObjectUrl(hash, ALIGNMENT_EXTENSION))
    if (response.status === NOT_FOUND) { return missing(true) }
    if (!response.ok) { return missing(false) }

    const payload = payloadFromDocument(text, await response.json(), clipObjectUrl(hash, AUDIO_EXTENSION))
    return payload ? { entry: payload, unrecorded: false, } : missing(true)
  } catch (error) {
    return missing(false)
  }
}

const resolveClip = async ({ source, text, }: SpeechRequest, watch: FetchWatcher, onMiss?: MissListener) => {
  const hash = await digestSpeechText(text)
  if (!hash) {
    entries.set(text, CLIP_STATE.UNSUPPORTED)
    return
  }

  const { entry, unrecorded, } = await fetchClip(text, hash, watch)
  entries.set(text, entry)
  if (unrecorded && source && onMiss) { onMiss(source, hash) }
}

export function registerClipTexts(requests: SpeechRequest[], { onMiss, watch = plainFetch }: { onMiss?: MissListener, watch?: FetchWatcher } = {}): Promise<void> {
  if (!clipsConfigured() || tooSlow) { return Promise.resolve() }

  const pending = new Map<string, SpeechRequest>()
  requests.forEach((request) => {
    if (request.text && request.text.trim() && !entries.has(request.text) && !pending.has(request.text)) { pending.set(request.text, request) }
  })
  pending.forEach((_, text) => entries.set(text, CLIP_STATE.PENDING))
  return Promise.all(Array.from(pending.values(), request => resolveClip(request, watch, onMiss))).then(() => undefined)
}

export const getClip = (text: string): ClipEntry | undefined => {
  if (!clipsConfigured()) { return CLIP_STATE.UNSUPPORTED }
  if (tooSlow) { return CLIP_STATE.STICKY }

  return entries.get(text)
}

export const resetClipRegistry = () => {
  entries.clear()
  baseUrl = null
  tooSlow = false
}
